/**
 * CitationPanel Component
 *
 * Modal dialog for viewing source document evidence when a citation is clicked.
 * Shows source location, quote, confidence, evidence type, and supporting/contradicting evidence.
 * Styled consistently with the FeedbackForm modal.
 *
 * Requirements: 14.1, 14.2, 14.3, 14.4, 14.5
 */

import React, { useCallback, useEffect, useRef } from 'react';
import {
  Dismiss20Regular,
  DocumentRegular,
  LocationRegular,
  NoteRegular,
  ShieldCheckmarkRegular,
  WarningRegular,
  InfoRegular,
  CheckmarkCircleRegular,
} from '@fluentui/react-icons';
import { Citation } from '../../types/extraction';
import styles from './CitationPanel.module.css';

export interface CitationPanelProps {
  /** The citation to display, or null if no citation is selected */
  citation: Citation | null;
  /** Whether the panel is open */
  isOpen: boolean;
  /** Callback to close the panel */
  onClose: () => void;
}

/**
 * Formats confidence as a percentage string
 */
const formatConfidence = (confidence: number): string => {
  return `${Math.round(confidence * 100)}%`;
};

/**
 * Returns a label for the evidence type
 */
const evidenceTypeLabel = (type: 'direct' | 'inferred'): string => {
  return type === 'direct' ? 'Direct Evidence' : 'Inferred Evidence';
};

/**
 * CitationPanel displays source document evidence in a modal dialog.
 *
 * Features:
 * - Source location and page/section reference
 * - Direct source quote with highlighting
 * - Evidence type badge (direct/inferred)
 * - Confidence score
 * - Supporting and contradicting evidence lists
 * - Assumptions with impact analysis
 * - Keyboard accessible (Escape to close)
 * - Modal overlay matching FeedbackForm pattern
 */
export const CitationPanel: React.FC<CitationPanelProps> = ({
  citation,
  isOpen,
  onClose,
}) => {
  const modalRef = useRef<HTMLDivElement>(null);

  // Focus trap and escape key
  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  const handleOverlayClick = useCallback(
    (e: React.MouseEvent) => {
      if (e.target === e.currentTarget) onClose();
    },
    [onClose]
  );

  if (!isOpen || !citation) return null;

  const hasEnhancedData = !!(
    citation.sourceLocation ||
    citation.sourceQuote ||
    citation.evidenceType ||
    citation.evidenceConfidence != null ||
    citation.supportingEvidence?.length ||
    citation.contradictingEvidence?.length ||
    citation.assumptions?.length
  );

  // Build reference string
  const reference = [
    citation.pageNumber != null ? `Page ${citation.pageNumber}` : null,
    citation.sectionReference,
  ]
    .filter(Boolean)
    .join(' • ');

  return (
    <div
      className={styles.overlay}
      onClick={handleOverlayClick}
      role="dialog"
      aria-modal="true"
      aria-label="Citation details"
    >
      <div className={styles.modal} ref={modalRef}>
        {/* Header */}
        <div className={styles.header}>
          <h3 className={styles.title}>
            <DocumentRegular className={styles.titleIcon} />
            Citation Details
          </h3>
          <button
            type="button"
            className={styles.closeButton}
            onClick={onClose}
            aria-label="Close citation details"
          >
            <Dismiss20Regular />
          </button>
        </div>

        {/* Document Name */}
        <div className={styles.documentSection}>
          <span className={styles.documentName}>{citation.documentName}</span>
          {reference && <span className={styles.reference}>{reference}</span>}
        </div>

        {/* Content */}
        <div className={styles.content}>
          {/* Source Location */}
          {citation.sourceLocation && (
            <div className={styles.section}>
              <div className={styles.sectionLabel}>
                <LocationRegular className={styles.sectionIcon} />
                Source Location
              </div>
              <div className={styles.sectionValue}>{citation.sourceLocation}</div>
            </div>
          )}

          {/* Source Quote */}
          {citation.sourceQuote && (
            <div className={styles.section}>
              <div className={styles.sectionLabel}>
                <NoteRegular className={styles.sectionIcon} />
                Source Quote
              </div>
              <blockquote className={styles.quote}>{citation.sourceQuote}</blockquote>
            </div>
          )}

          {/* Evidence Type & Confidence Row */}
          {(citation.evidenceType || citation.evidenceConfidence != null) && (
            <div className={styles.metaRow}>
              {citation.evidenceType && (
                <span
                  className={`${styles.badge} ${
                    citation.evidenceType === 'direct'
                      ? styles.badgeDirect
                      : styles.badgeInferred
                  }`}
                >
                  {citation.evidenceType === 'direct' ? (
                    <CheckmarkCircleRegular className={styles.badgeIcon} />
                  ) : (
                    <InfoRegular className={styles.badgeIcon} />
                  )}
                  {evidenceTypeLabel(citation.evidenceType)}
                </span>
              )}
              {citation.evidenceConfidence != null && (
                <span className={styles.confidenceBadge}>
                  <ShieldCheckmarkRegular className={styles.badgeIcon} />
                  Confidence: {formatConfidence(citation.evidenceConfidence)}
                </span>
              )}
            </div>
          )}

          {/* Supporting Evidence */}
          {citation.supportingEvidence && citation.supportingEvidence.length > 0 && (
            <div className={styles.section}>
              <div className={styles.sectionLabel}>
                <CheckmarkCircleRegular className={styles.sectionIcon} />
                Supporting Evidence
              </div>
              <ul className={styles.evidenceList}>
                {citation.supportingEvidence.map((item, i) => (
                  <li key={i} className={styles.evidenceItem}>
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Contradicting Evidence */}
          {citation.contradictingEvidence && citation.contradictingEvidence.length > 0 && (
            <div className={styles.section}>
              <div className={styles.sectionLabel}>
                <WarningRegular className={styles.sectionIcon} />
                Contradicting Evidence
              </div>
              <ul className={`${styles.evidenceList} ${styles.contradicting}`}>
                {citation.contradictingEvidence.map((item, i) => (
                  <li key={i} className={styles.evidenceItem}>
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Assumptions */}
          {citation.assumptions && citation.assumptions.length > 0 && (
            <div className={styles.section}>
              <div className={styles.sectionLabel}>
                <InfoRegular className={styles.sectionIcon} />
                Assumptions
              </div>
              <div className={styles.assumptionsList}>
                {citation.assumptions.map((assumption, i) => (
                  <div key={i} className={styles.assumption}>
                    <p className={styles.assumptionDesc}>{assumption.description}</p>
                    {assumption.impactIfWrong && (
                      <p className={styles.assumptionImpact}>
                        <WarningRegular className={styles.impactIcon} />
                        <span>{assumption.impactIfWrong}</span>
                      </p>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Fallback: show excerpt if no enhanced data */}
          {!hasEnhancedData && citation.excerpt && (
            <div className={styles.section}>
              <div className={styles.sectionLabel}>
                <NoteRegular className={styles.sectionIcon} />
                Excerpt
              </div>
              <blockquote className={styles.quote}>{citation.excerpt}</blockquote>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className={styles.footer}>
          <button type="button" className={styles.closeFooterButton} onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default CitationPanel;
